import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { ArrowLeft, Calendar, Clock, User, Tag, Share2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  // Define removeJsonLd first since injectJsonLd depends on it
  const removeJsonLd = useCallback(() => {
    const el = document.getElementById('blog-jsonld');
    if (el) el.remove();
  }, []);

  const updateMeta = useCallback((name, content) => {
    const isOg = name.startsWith('og:') || name.startsWith('article:');
    let el = isOg
      ? document.querySelector(`meta[property="${name}"]`)
      : document.querySelector(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      if (isOg) el.setAttribute('property', name);
      else el.setAttribute('name', name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  }, []);

  const injectJsonLd = useCallback((p) => {
    removeJsonLd();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'blog-jsonld';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": p.title,
      "description": p.seo_description || p.excerpt,
      "image": p.featured_image || '',
      "author": { "@type": "Organization", "name": p.author || "RunTheRays Team" },
      "publisher": {
        "@type": "Organization",
        "name": "Shanghai RunTheRays International Trade Co., Ltd.",
        "url": "https://runtheraysintl.com"
      },
      "datePublished": p.published_at,
      "dateModified": p.updated_at || p.published_at,
      "keywords": (p.tags || []).join(', ')
    });
    document.head.appendChild(script);
  }, [removeJsonLd]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const { data } = await axios.get(`${API}/api/blog/posts/slug/${slug}`);
        setPost(data.data);
        // Fetch related posts
        if (data.data.id) {
          try {
            const rel = await axios.get(`${API}/api/blog/posts/${data.data.id}/related`);
            setRelated(rel.data.data || []);
          } catch { /* non-critical */ }
        }
        // SEO: Update document title and meta tags
        const p = data.data;
        document.title = p.seo_title || p.title;
        updateMeta('description', p.seo_description || p.excerpt || '');
        updateMeta('og:title', p.seo_title || p.title);
        updateMeta('og:description', p.seo_description || p.excerpt || '');
        updateMeta('og:type', 'article');
        if (p.featured_image) updateMeta('og:image', p.featured_image);
        updateMeta('article:published_time', p.published_at);
        updateMeta('article:author', p.author);
        // JSON-LD structured data
        injectJsonLd(p);
      } catch (err) { console.error('Failed to fetch blog post:', err); }
      finally { setLoading(false); }
    };
    fetchPost();
    return () => {
      document.title = 'Shanghai RunTheRays International Trade Co., Ltd.';
      removeJsonLd();
    };
  }, [slug, updateMeta, injectJsonLd, removeJsonLd]);

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: post.title, url: window.location.href }); } catch (err) { console.error('Share failed:', err); }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      alert('Link copied!');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!post) return <div className="min-h-screen flex flex-col items-center justify-center"><p className="text-lg mb-4">Article not found</p><Link to="/blog" className="text-orange-600 hover:underline">Back to blog</Link></div>;

  // Sanitize content with DOMPurify before rendering
  const sanitizedContent = DOMPurify.sanitize(post.content);

  return (
    <div className="min-h-screen bg-white">
      {/* SEO meta tags injected via useEffect */}
      <header className="bg-blue-900 text-white py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link to="/blog" className="text-orange-400 hover:text-orange-300 text-sm font-medium mb-6 inline-flex items-center gap-1" data-testid="back-to-blog">
            <ArrowLeft size={16} /> Back to Trade Insights
          </Link>
          <span className="block text-orange-400 text-xs font-semibold uppercase tracking-wider mb-3">{post.category}</span>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 leading-tight" data-testid="blog-post-title">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-blue-200 text-sm">
            <span className="flex items-center gap-1.5"><User size={14} />{post.author}</span>
            <span className="flex items-center gap-1.5"><Calendar size={14} />{new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} />{post.reading_time} min read</span>
            <button onClick={share} className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="blog-share-btn">
              <Share2 size={14} /> Share
            </button>
          </div>
        </div>
      </header>

      {post.featured_image && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-6">
          <img src={post.featured_image} alt={post.title} className="w-full h-64 md:h-96 object-cover rounded-xl shadow-lg" />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div
          className="prose prose-lg max-w-none prose-headings:text-blue-900 prose-headings:font-bold prose-a:text-orange-600 prose-strong:text-gray-900 prose-p:text-gray-600 prose-li:text-gray-600"
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          data-testid="blog-post-content"
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-10 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag size={16} className="text-gray-400" />
              {post.tags.map(tag => (
                <Link key={tag} to={`/blog?tag=${tag}`} className="text-xs bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-orange-600 px-3 py-1 rounded-full transition-colors">
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-3">Need Help With Your Supply Chain?</h3>
          <p className="text-blue-200 mb-6">Our experts are ready to help you source, inspect, and ship with confidence.</p>
          <Link to="/#quote" className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors">
            Get a Free Quote
          </Link>
        </div>
      </article>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12" data-testid="related-articles">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Related Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map(r => (
              <Link
                key={r.id}
                to={`/blog/${r.slug}`}
                className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`related-article-${r.slug}`}
              >
                {r.featured_image && (
                  <img src={r.featured_image} alt={r.title} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" />
                )}
                <div className="p-4">
                  <span className="text-xs font-semibold text-orange-600 uppercase">{r.category}</span>
                  <h3 className="text-sm font-bold text-gray-900 mt-1 line-clamp-2 group-hover:text-blue-900 transition-colors">{r.title}</h3>
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Clock size={12} />{r.reading_time} min</span>
                    <span className="flex items-center gap-1"><Calendar size={12} />{new Date(r.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <footer className="bg-gray-50 border-t py-8 text-center text-gray-500 text-sm">
        <p>&copy; {new Date().getFullYear()} Shanghai RunTheRays International Trade Co., Ltd.</p>
      </footer>
    </div>
  );
};

export default BlogPost;
